import {
    AppBar,
    Dialog,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Toolbar,
    Tooltip,
} from "@material-ui/core";
import * as React from "react";

import {
    Button,
    Loading,
    LoadingMessageType,
    PageActionButton,
    ScrollContainer,
    Text,
    useRelodeAppUI,
} from "../";
import {
    ActiveWizardCheckIcon,
    CompleteWizardCheckIcon,
    ExclamationIcon,
    InactiveWizardCheckIcon,
    TimesIcon,
} from "../../assets/icons";
import { Label } from "../label";
import { AreaNotification } from "../notifications";
import { useStyles } from "./styles";
import { WizardStepProps } from "./wizardStep";

export interface WizardProps {
    id: string;
    title: string;
    header?: React.ReactNode;
    hasError: boolean;
    errorMessage?: string;
    actions?: React.ReactNode;
    activeStepId: string;
    children?: React.ReactNode;
    isLoading: boolean;
    loadingDisplayText?: string;
    loadingMessageType?: LoadingMessageType;
    errorAreaName?: string;
    customNextButtonLabel?: string;
    customFinishButtonLabel?: string;
    onCancel: () => void;
    onFinish: () => void;
    onNext: () => void;
    onPrevious: () => void;
    onSaveAndExit?: () => void;
    presenter?: (props: { children: React.ReactNode }) => React.ReactNode;
}

export const Wizard = (props: WizardProps) => {
    const errorAreaName = !!props.errorAreaName
        ? props.errorAreaName
        : "wizardErrorArea";
    const getStepChildren = () => {
        const s: WizardStepProps[] = [];

        React.Children.forEach(
            props.children,
            (child: React.ReactElement<WizardStepProps>) => {
                if (child) {
                    s.push({ ...child.props });
                }
            }
        );
        return s;
    };

    const stepChildren = getStepChildren();
    const activeStepIndex = stepChildren.findIndex(
        step => step.id === props.activeStepId
    );
    const activeStep = stepChildren[activeStepIndex];
    const classes = useStyles({});

    const { ui } = useRelodeAppUI();

    React.useEffect(() => {
        ui.actions.setPageTitle(props.title);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.title]);

    const handleNext = () => {
        if (props.isLoading === false) {
            props.onNext();
        }
    };

    const handlePrevious = () => {
        if (props.isLoading === false) {
            props.onPrevious();
        }
    };

    const handleSaveAndExit = () => {
        if (props.isLoading === false) {
            props.onSaveAndExit();
        }
    };

    const getStepIcon = (index: number) => {
        if (index < activeStepIndex) return CompleteWizardCheckIcon;
        if (index === activeStepIndex) return ActiveWizardCheckIcon;
        return InactiveWizardCheckIcon;
    };

    const renderStepLabel = (step: WizardStepProps, index: number) => (
        <StepLabel
            error={!!step.hasError}
            data-cy={
                step.hasError
                    ? `${props.id}-${step.id}-stepper-has-error`
                    : `${props.id}-${step.id}-stepper-has-no-error`
            }
            StepIconComponent={getStepIcon(index)}
            StepIconProps={{
                active: undefined,
                completed: undefined,
                error: undefined,
            }}
        >
            <span className={classes.stepLabel}>{`step ${index + 1}`}</span>
        </StepLabel>
    );

    const renderStep = (step: WizardStepProps, index: number) => (
        <Step
            key={`${index}-${step.title}`}
            data-cy={`${props.id}-${step.id}-stepper`}
            active={true}
        >
            {renderStepLabel(step, index)}
            <StepContent>
                {index !== activeStepIndex ? (
                    <span className={classes.stepInactive}>{step.title}</span>
                ) : (
                    <Text variant="h3">{step.title}</Text>
                )}
            </StepContent>
        </Step>
    );

    const renderNextStepButton = () => {
        if (activeStepIndex < stepChildren.length - 1) {
            return (
                <Button
                    data-cy="wizard-next-button"
                    onClick={handleNext}
                    size="small"
                    color="primary"
                    variant="contained"
                    title="next step"
                    id="nextButton"
                    disabled={props.isLoading}
                    className={classes.button}
                >
                    {props?.customNextButtonLabel ?? "next"}
                </Button>
            );
        } else {
            return (
                <Button
                    data-cy="wizard-finish-button"
                    onClick={props.onFinish}
                    size="small"
                    color="primary"
                    variant="contained"
                    title="finish"
                    id="finishButton"
                    disabled={props.isLoading}
                    className={classes.button}
                >
                    {props?.customFinishButtonLabel ?? "finish"}
                </Button>
            );
        }
    };

    const renderSaveAndExitButton = () => (
        <Button
            data-cy="wizard-saveAndExit-button"
            onClick={handleSaveAndExit}
            size="small"
            color="default"
            variant="contained"
            title="save and exit"
            id="saveAndExitButton"
            disabled={props.isLoading}
            className={classes.button}
        >
            save and exit
        </Button>
    );

    const renderPrevStepButton = () =>
        activeStepIndex > 0 ? (
            <Button
                data-cy="wizard-previous-button"
                onClick={handlePrevious}
                color="default"
                size="small"
                variant="contained"
                title="previous step"
                id="previousButton"
                disabled={props.isLoading}
                className={classes.button}
            >
                back
            </Button>
        ) : null;

    const renderCancelButton = () => (
        <PageActionButton
            data-cy="wizard-cancel-button"
            onClick={props.onCancel}
            title="cancel"
            color="primary"
            disabled={props.isLoading}
            className={classes.cancelButton}
        >
            <TimesIcon />
        </PageActionButton>
    );

    const renderActiveStep = () => {
        if (!activeStep) return null;
        return (
            <div
                className={classes.wizardContentArea}
                data-cy={`${props.id}-${activeStep.id}`}
            >
                <div className={classes.step} key={activeStep.id}>
                    {activeStep.component}
                </div>
            </div>
        );
    };

    return !!activeStep && stepChildren.length > 0 ? (
        <Dialog
            data-cy={`${props.id}-dialog`}
            id={`${props.id}-dialog`}
            fullScreen
            open
            scroll="paper"
            classes={{
                paper: classes.paper,
            }}
        >
            <AppBar className={classes.appBar} color="inherit">
                <Toolbar>
                    <div className={classes.titleWrapper}>
                        {!!props.header ? (
                            <>{props.header}</>
                        ) : (
                            <Text
                                variant="h2"
                                color="primary"
                                className={classes.title}
                            >
                                {props.title}
                            </Text>
                        )}
                        {props.hasError ? (
                            <Tooltip
                                placement="bottom-start"
                                title="please correct the errors in the indicated step(s)"
                                data-cy="wizardTitle-error-title"
                            >
                                <span>
                                    <ExclamationIcon
                                        data-cy="wizardTitle-error-exclamation-icon"
                                        color="error"
                                        className={classes.errorIcon}
                                    />
                                </span>
                            </Tooltip>
                        ) : null}
                        {props.actions ? props.actions : null}
                    </div>
                    <div style={{ flexGrow: 1 }} />
                    {renderPrevStepButton()}
                    {renderNextStepButton()}
                    {props.onSaveAndExit ? renderSaveAndExitButton() : null}
                    {renderCancelButton()}
                </Toolbar>
            </AppBar>
            <ScrollContainer>
                <div className={classes.content}>
                    <div className={classes.stepper}>
                        <Stepper
                            activeStep={activeStepIndex}
                            orientation="vertical"
                            className={classes.steps}
                        >
                            {stepChildren.map((s, index) =>
                                renderStep(s, index)
                            )}
                        </Stepper>
                    </div>
                    <div className={classes.stepContentArea}>
                        <AreaNotification
                            areaName={errorAreaName || "wizardErrorArea"}
                        />
                        <Loading
                            showIndicator
                            fillSpace
                            displayText={props.loadingDisplayText}
                            isLoading={props.isLoading}
                            variant="blur"
                            messageType={props.loadingMessageType}   
                        >
                            <div className={classes.stepContainer}>
                                {props.presenter ? (
                                    <>
                                        {(props.presenter as (props: {
                                            children: React.ReactNode;
                                        }) => React.ReactNode)({
                                            children: renderActiveStep(),
                                        })}
                                    </>
                                ) : (
                                    renderActiveStep()
                                )}
                            </div>
                        </Loading>
                    </div>
                </div>
            </ScrollContainer>
        </Dialog>
    ) : null;
};
